import React, { useState, useEffect, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Mail, MapPin, Music, Sparkles, Loader2, Play, Pause, Plane, Car, Phone, ExternalLink, Check,
  Clock, Hotel, Utensils, Shirt, MapPinned, ArrowRight, Timer, FileText, ChevronDown, MoreHorizontal,
  Pencil, Eye, Download, Send, Trash2, Users, Calendar, PhoneCall, X
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FinalInstrumentEditor } from "./FinalInstrumentEditor";
import { TechRiderGenerator } from "./TechRiderGenerator";
import { useWorkflowStages } from "./WorkflowStagePanel";
import { MusicianAssignmentPanel } from "./MusicianAssignmentPanel";
import { logActivity } from "@/lib/activityLogger";
import { generateDocumentNumber, getEventDateFromDocument, getDocumentTypeLabel } from "@/lib/documentNaming";
import { generateTimelinePdf } from "@/lib/nativePdfGenerator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { majorAirports, calculateDistance, findNearestAirport, getAirportTimezoneAbbr, type Airport } from "@/lib/airports";
import { AirportSearch } from "@/components/admin/AirportSearch";
import { parse, differenceInDays, isValid, format, subDays, addDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { FlightSearchLinks } from "@/components/admin/FlightSearchLinks";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import { MiniAudioPlayer } from "@/components/MiniAudioPlayer";
import { TravelTimeline } from "@/components/admin/TravelTimeline";
import { 
  calculateFlightTime, 
  analyzeSameDayArrival, 
  calculateLandingTime,
  formatMinutesToTime,
  type FlightTimeResult 
} from "@/lib/flightCalculations";

interface Package {
  name: string;
  price: number;
  musicians?: string[] | string;
  description?: string;
  eventIndex?: number;
  ensembleSize?: number;
}

interface CulturalAnalysis {
  primaryCulture?: string;
  customerMessage?: string;
  reasoning?: string;
  analysis?: string;
  recommendedInstruments?: string[];
  recommendedMoments?: string[];
  confidence?: number;
  occasion?: string;
  vibes?: string[];
  venue?: string;
  startTime?: string;
  endTime?: string;
  genres?: string[];
  eventIndex?: number;
}

interface Song {
  song_title?: string;
  artist?: string;
  album?: string;
  spotify_uri?: string;
  event_index?: number;
  preview_url?: string;
  image_url?: string;
}

interface VenueLocation {
  address: string;
  lat?: number;
  lng?: number;
  nearestAirport?: { airport: Airport; distance: number };
  eventIndex: number;
  date?: Date;
  startTime?: string;
}

interface TravelItinerary {
  tripNumber: number;
  events: { index: number; occasion: string; date: string; venue: VenueLocation; startTime?: string; endTime?: string }[];
  arrivalAirport?: { airport: Airport; distance: number };
  departureAirport?: { airport: Airport; distance: number };
  recommendedArrivalDate?: Date;
  recommendedDepartureDate?: Date;
  departureIsNextDay?: boolean;
  isSameDayDeparture?: boolean;
  useRedEyeInbound?: boolean; // True if taking overnight flight TO the event
  inboundDepartureMinutes?: number; // Departure time for inbound flight (negative = previous day)
  inboundDepartureDayOffset?: number; // -1 = previous day, 0 = same day
  flightTime?: FlightTimeResult | null; // Timezone-aware flight calculations
  landingDayOffset?: number; // Days after departure that we land (0, 1, or 2 for international)
}

interface InquiryDetailTabsProps {
  inquiry: any;
  formatTime: (time: string) => string;
  formatCurrency: (amount: number | null) => string;
  safeFormatEventDate: (value: unknown, fmt: string) => string;
  onRefresh?: () => void;
  onOpenContract?: () => void;
  contracts?: any[];
  musicianAssignments?: any[];
  onEditContract?: (contract: any) => void;
  onEditItinerary?: (contract: any) => void;
  onPreviewContract?: (storagePath: string, contractNumber: string) => void;
  onDownloadContract?: (storagePath: string, contractNumber: string) => void;
  onAttachToEmail?: (contract: any) => void;
  onSendForSignature?: (contract: any) => void;
  onDeleteContract?: (contract: any) => void;
  onScheduleCall?: () => void;
  onCancelCall?: () => void;
}

export function InquiryDetailTabs({ 
  inquiry, 
  formatTime, 
  formatCurrency, 
  safeFormatEventDate,
  onRefresh,
  onOpenContract,
  contracts = [],
  musicianAssignments = [],
  onEditContract,
  onEditItinerary,
  onPreviewContract,
  onDownloadContract,
  onAttachToEmail,
  onSendForSignature,
  onDeleteContract,
  onScheduleCall,
  onCancelCall
}: InquiryDetailTabsProps) {
  const [runningAnalysis, setRunningAnalysis] = useState<number | null>(null);
  const [venueLocations, setVenueLocations] = useState<VenueLocation[]>([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [viewerOriginAirport, setViewerOriginAirport] = useState<Airport | null>(null);
  const [manualOriginCode, setManualOriginCode] = useState<string>('');
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [savingItinerary, setSavingItinerary] = useState(false);
  const [savingInstruments, setSavingInstruments] = useState<number | null>(null);
  const [travelExpanded, setTravelExpanded] = useState(false);
  
  // State for saving
  
  // Use global audio player context (same as taste builder)
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    progress,
    playTrack,
    togglePlayPause,
    stopPlayback,
  } = useAudioPlayer();
  
  const hasSongs = inquiry.songs_json && Array.isArray(inquiry.songs_json) && inquiry.songs_json.length > 0;
  const hasPackages = inquiry.packages_json && Array.isArray(inquiry.packages_json) && inquiry.packages_json.length > 0;

  // Detect viewer's origin airport
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke('get-client-location');
        if (!isMounted) return;
        if (data?.success && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
          const nearest = findNearestAirport(data.latitude, data.longitude);
          setViewerOriginAirport(nearest.airport);
        }
      } catch { /* ignore */ }
    })();
    return () => { isMounted = false; };
  }, []);

  // Get events from event_dates_json or create single event from legacy fields
  const events = inquiry.event_dates_json && inquiry.event_dates_json.length > 0
    ? inquiry.event_dates_json
    : [{
        date: inquiry.event_date,
        occasion: inquiry.occasion,
        startTime: inquiry.event_start_time,
        endTime: inquiry.event_end_time,
        vibes: inquiry.vibes,
        venue: inquiry.venue_address
      }];

  const parseEventDate = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined;
    let parsed = parse(dateStr, 'M/d/yyyy', new Date());
    if (isValid(parsed)) return parsed;
    parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) return parsed;
    const native = new Date(dateStr);
    if (isValid(native)) return native;
    return undefined;
  };

  const getEventVenues = (): { address: string; eventIndex: number; date?: Date; startTime?: string }[] => {
    return events.map((event: any, idx: number) => {
      const venue = event.venueAddress || event.venue || inquiry.venue_address;
      const date = parseEventDate(event.date);
      return { address: venue && venue !== 'To be determined' ? venue : '', eventIndex: idx, date, startTime: event.startTime || inquiry.event_start_time };
    }).filter(v => v.address);
  };

  // Geocode venues
  useEffect(() => {
    const geocodeVenues = async () => {
      const eventVenues = getEventVenues();
      if (eventVenues.length === 0) return;
      setLoadingVenues(true);
      const locations: VenueLocation[] = [];
      for (const { address, eventIndex, date, startTime } of eventVenues) {
        try {
          const { data, error } = await supabase.functions.invoke('geocode-venue', { body: { address } });
          if (data && !error) {
            const nearestAirport = findNearestAirport(data.lat, data.lng);
            locations.push({ address, lat: data.lat, lng: data.lng, nearestAirport, eventIndex, date, startTime });
          } else {
            locations.push({ address, eventIndex, date, startTime });
          }
        } catch {
          locations.push({ address, eventIndex, date, startTime });
        }
      }
      setVenueLocations(locations);
      setLoadingVenues(false);
    };
    geocodeVenues();
  }, [inquiry.id]);

  // Build travel itineraries with smart arrival date calculation (TIMEZONE AWARE)
  const travelItineraries = useMemo((): TravelItinerary[] => {
    if (venueLocations.length === 0) return [];
    const sorted = [...venueLocations].sort((a, b) => {
      if (a.date && b.date) return a.date.getTime() - b.date.getTime();
      return a.eventIndex - b.eventIndex;
    });

    const itineraries: TravelItinerary[] = [];
    let currentTrip: TravelItinerary | null = null;
    const originCode = manualOriginCode || viewerOriginAirport?.code;

    sorted.forEach((venue, idx) => {
      const prevVenue = idx > 0 ? sorted[idx - 1] : null;
      const event = events[venue.eventIndex];
      let isNewTrip = !currentTrip;
      
      // Trip grouping: consecutive days (diff of 1) are same trip, 2+ days apart is a new trip
      if (prevVenue && venue.date && prevVenue.date) {
        const daysDiff = differenceInDays(venue.date, prevVenue.date);
        // If more than 1 day gap, start a new trip (consecutive = same trip)
        if (daysDiff > 1) isNewTrip = true;
      }

      if (isNewTrip) {
        if (currentTrip) itineraries.push(currentTrip);
        
        // Smart arrival date calculation with TIMEZONE AWARENESS
        const eventStartTime = venue.startTime || '18:00';
        const [h, m] = eventStartTime.split(':').map(Number);
        const eventStartMinutes = h * 60 + (m || 0);
        
        const driveToVenueMinutes = venue.nearestAirport?.distance 
          ? Math.ceil((venue.nearestAirport.distance / 40) * 60) 
          : 30;
        
        const destCode = venue.nearestAirport?.airport.code;
        
        // Use timezone-aware analysis
        const arrivalAnalysis = originCode && destCode 
          ? analyzeSameDayArrival(originCode, destCode, eventStartMinutes, driveToVenueMinutes)
          : null;
        
        // Get flight time data for later use
        const flightTime = originCode && destCode 
          ? calculateFlightTime(originCode, destCode) 
          : null;
        
        // Determine arrival strategy based on timezone-aware analysis
        let recommendedArrival = venue.date;
        let useRedEyeInbound = false;
        let inboundDepartureMinutes = 0;
        let inboundDepartureDayOffset = 0;
        let landingDayOffset = 0;
        
        if (arrivalAnalysis && venue.date) {
          useRedEyeInbound = arrivalAnalysis.useRedEyeInbound;
          inboundDepartureMinutes = arrivalAnalysis.requiredDepartureMinutes;
          
          // Calculate actual landing day offset using flight calculations
          if (flightTime && inboundDepartureMinutes !== undefined) {
            // Normalize departure time to positive value for calculation
            let depMins = inboundDepartureMinutes;
            if (depMins < 0) {
              depMins = 1440 + depMins; // Convert to previous day time
              inboundDepartureDayOffset = -1;
            }
            const landing = calculateLandingTime(depMins, flightTime);
            landingDayOffset = landing.dayOffset + (inboundDepartureDayOffset < 0 ? 1 : 0); // Adjust for previous day departure
          }
          
          if (arrivalAnalysis.canArriveSameDay) {
            recommendedArrival = venue.date; // Land on event day
          } else {
            recommendedArrival = subDays(venue.date, 1); // Arrive day before
          }
        }
        
        currentTrip = {
          tripNumber: itineraries.length + 1,
          events: [],
          arrivalAirport: venue.nearestAirport,
          recommendedArrivalDate: recommendedArrival,
          useRedEyeInbound,
          inboundDepartureMinutes,
          inboundDepartureDayOffset,
          flightTime,
          landingDayOffset,
        };
      }

      currentTrip!.events.push({
        index: venue.eventIndex,
        occasion: event?.occasion || `Event ${venue.eventIndex + 1}`,
        date: event?.date || '',
        venue,
        startTime: venue.startTime,
        endTime: event?.endTime
      });

      currentTrip!.departureAirport = venue.nearestAirport;
      
      // Smart departure: check if red-eye/same-night departure is feasible
      // Red-eye strategy: head straight to airport after event, catch overnight flight home
      const eventEndTime = event?.endTime || '22:00';
      const [endH, endM] = eventEndTime.split(':').map(Number);
      const eventEndMinutes = endH * 60 + (endM || 0);
      
      // Calculate soonest possible departure time (skip hotel, go directly to airport)
      const PACK_UP_TIME = 30; // Pack up, eat, goodbyes
      const driveToAirportMins = venue.nearestAirport?.distance 
        ? Math.ceil((venue.nearestAirport.distance / 40) * 60) 
        : 30;
      const AIRPORT_BUFFER = 120; // 2 hours before flight
      
      // Direct to airport timeline: event end → pack up → drive to airport → check-in
      const soonestDepartureMinutes = eventEndMinutes + PACK_UP_TIME + driveToAirportMins + AIRPORT_BUFFER;
      
      // Red-eye departures: Allow flights up to 6 AM next day (30 hours from midnight = 1800 min)
      // This means after an 11 PM event end, you could catch a 3:15 AM red-eye
      const LATEST_REDEYE_FLIGHT = 30 * 60; // 6 AM next day = 30 hours from midnight
      const canDepartSameNight = soonestDepartureMinutes <= LATEST_REDEYE_FLIGHT;
      
      // For red-eye: departure date is event date (flight crosses midnight)
      // For next-day: departure date is day after event
      currentTrip!.recommendedDepartureDate = venue.date 
        ? (canDepartSameNight ? venue.date : addDays(venue.date, 1))
        : undefined;
      currentTrip!.departureIsNextDay = !canDepartSameNight;
      currentTrip!.isSameDayDeparture = canDepartSameNight;
    });

    if (currentTrip) itineraries.push(currentTrip);
    return itineraries;
  }, [venueLocations, events, manualOriginCode, viewerOriginAirport]);

  // Calculate hour breakdowns for travel itinerary summary
  const itinerarySummary = useMemo(() => {
    if (travelItineraries.length === 0) return null;
    
    let totalTravelMinutes = 0; // Flights + drives
    let totalEventMinutes = 0; // Performance time
    let totalItineraryMinutes = 0; // Everything from first departure to final return
    
    const originCode = manualOriginCode || viewerOriginAirport?.code;
    
    travelItineraries.forEach((trip) => {
      const arrCode = trip.arrivalAirport?.airport.code;
      const isLocal = originCode && arrCode && originCode === arrCode;
      
      // TRAVEL CALCULATION
      if (isLocal) {
        // Local event: round-trip drive only (no flights)
        const driveToVenue = trip.arrivalAirport?.distance 
          ? Math.ceil((trip.arrivalAirport.distance / 40) * 60) : 30;
        totalTravelMinutes += driveToVenue * 2; // Round trip drive
      } else {
        // Flight event: round-trip flights + drives to/from airports
        const flightDuration = trip.flightTime?.flightDurationMinutes ?? 180;
        totalTravelMinutes += flightDuration * 2; // Round trip flights
        
        const driveToVenue = trip.arrivalAirport?.distance 
          ? Math.ceil((trip.arrivalAirport.distance / 40) * 60) : 30;
        const driveFromVenue = trip.departureAirport?.distance 
          ? Math.ceil((trip.departureAirport.distance / 40) * 60) : 30;
        totalTravelMinutes += driveToVenue + driveFromVenue;
      }
      
      // EVENT HOURS: setup/soundcheck (2h) + performance + pack up (30min) per event
      const SETUP_SOUNDCHECK = 120;
      const PACK_UP = 30;
      
      trip.events.forEach((event) => {
        const parseTime = (timeStr: string) => {
          const [h, m] = (timeStr || '18:00').split(':').map(Number);
          return h * 60 + (m || 0);
        };
        const startMinutes = parseTime(event.startTime || '18:00');
        const endMinutes = parseTime(event.endTime || '23:00');
        const performanceDuration = endMinutes > startMinutes 
          ? endMinutes - startMinutes 
          : (1440 - startMinutes) + endMinutes;
        totalEventMinutes += SETUP_SOUNDCHECK + performanceDuration + PACK_UP;
      });
    });
    
    // Total = Travel + Event (clean sum, no artificial overhead)
    totalItineraryMinutes = totalTravelMinutes + totalEventMinutes;
    
    const formatHours = (mins: number) => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    };
    
    return {
      totalHours: formatHours(totalItineraryMinutes),
      travelHours: formatHours(totalTravelMinutes),
      eventHours: formatHours(totalEventMinutes),
      tripCount: travelItineraries.length
    };
  }, [travelItineraries, manualOriginCode, viewerOriginAirport]);

  // Get helpers
  const getAnalysisForEvent = (eventIndex: number): CulturalAnalysis | null => {
    if (!inquiry.cultural_analysis_json) return null;
    if (Array.isArray(inquiry.cultural_analysis_json)) {
      return inquiry.cultural_analysis_json.find((a: CulturalAnalysis) => a.eventIndex === eventIndex) || inquiry.cultural_analysis_json[eventIndex] || null;
    }
    return eventIndex === 0 ? inquiry.cultural_analysis_json : null;
  };

  const getPackagesForEvent = (eventIndex: number): Package[] => {
    if (!hasPackages) return [];
    return inquiry.packages_json.filter((pkg: Package) => 
      pkg.eventIndex === eventIndex || (pkg.eventIndex === undefined && eventIndex === 0)
    );
  };

  const getSongsForEvent = (eventIndex: number): Song[] => {
    if (!hasSongs) return [];
    return inquiry.songs_json.filter((song: Song) => 
      song.event_index === eventIndex || (song.event_index === undefined && eventIndex === 0)
    );
  };

  // Get documents for a specific event by matching event date
  const getDocumentsForEvent = (eventIndex: number): any[] => {
    if (!contracts || contracts.length === 0) return [];
    const event = events[eventIndex];
    if (!event?.date) return [];
    
    // Parse event date to YYYY-MM-DD format for comparison
    const eventDate = parseEventDate(event.date);
    if (!eventDate) return [];
    const eventDateStr = format(eventDate, 'yyyy-MM-dd');
    
    return contracts.filter((contract: any) => {
      const docEventDate = getEventDateFromDocument(contract);
      return docEventDate === eventDateStr;
    });
  };

  // Get documents that don't match any event (orphaned/ungrouped)
  const getUngroupedDocuments = (): any[] => {
    if (!contracts || contracts.length === 0) return [];
    
    // Get all event dates
    const eventDates = events.map((event: any, idx: number) => {
      const eventDate = parseEventDate(event.date);
      return eventDate ? format(eventDate, 'yyyy-MM-dd') : null;
    }).filter(Boolean);
    
    return contracts.filter((contract: any) => {
      const docEventDate = getEventDateFromDocument(contract);
      return !docEventDate || !eventDates.includes(docEventDate);
    });
  };

  const runAnalysisForEvent = async (eventIndex: number) => {
    setRunningAnalysis(eventIndex);
    try {
      const event = events[eventIndex];
      const { data, error } = await supabase.functions.invoke('analyze-event-culture', {
        body: {
          occasion: event.occasion || inquiry.occasion,
          vibes: event.vibes || inquiry.vibes,
          startTime: event.startTime || inquiry.event_start_time,
          endTime: event.endTime || inquiry.event_end_time,
          eventIndex
        }
      });
      if (error) throw error;
      
      const existingAnalyses = inquiry.cultural_analysis_json 
        ? (Array.isArray(inquiry.cultural_analysis_json) ? inquiry.cultural_analysis_json : [inquiry.cultural_analysis_json])
        : [];
      const newAnalysis = { ...data, eventIndex };
      const filteredAnalyses = existingAnalyses.filter((a: CulturalAnalysis) => a.eventIndex !== eventIndex);
      const updatedAnalyses = [...filteredAnalyses, newAnalysis];
      
      await supabase.from('contact_submissions').update({ cultural_analysis_json: updatedAnalyses }).eq('id', inquiry.id);
      toast.success('Analysis complete');
      onRefresh?.();
    } catch (err: any) {
      toast.error(err.message || 'Analysis failed');
    } finally {
      setRunningAnalysis(null);
    }
  };

  // Local optimistic state for events (to avoid full page refresh)
  const [localEvents, setLocalEvents] = useState<any[]>(() => {
    return inquiry.event_dates_json && inquiry.event_dates_json.length > 0
      ? inquiry.event_dates_json
      : [{
          date: inquiry.event_date,
          occasion: inquiry.occasion,
          startTime: inquiry.event_start_time,
          endTime: inquiry.event_end_time,
          vibes: inquiry.vibes,
          venue: inquiry.venue_address
        }];
  });

  // Sync local events when inquiry changes from external source
  useEffect(() => {
    const newEvents = inquiry.event_dates_json && inquiry.event_dates_json.length > 0
      ? inquiry.event_dates_json
      : [{
          date: inquiry.event_date,
          occasion: inquiry.occasion,
          startTime: inquiry.event_start_time,
          endTime: inquiry.event_end_time,
          vibes: inquiry.vibes,
          venue: inquiry.venue_address
        }];
    setLocalEvents(newEvents);
  }, [inquiry.id, inquiry.event_dates_json]);

  // Save final instrument selection for an event (OPTIMISTIC - no page refresh)
  // Also auto-transitions status to 'contacted' if currently pending/call_scheduled
  const saveFinalInstruments = async (eventIndex: number, instruments: string[]) => {
    setSavingInstruments(eventIndex);
    try {
      const normalized = instruments
        .flatMap((item) =>
          item.includes(",")
            ? item.split(",").map((s) => s.trim()).filter(Boolean)
            : item
        )
        .filter(Boolean);

      // Optimistically update local state immediately
      setLocalEvents(prev => {
        const updated = [...prev];
        if (updated[eventIndex]) {
          updated[eventIndex] = { ...updated[eventIndex], finalInstruments: normalized };
        }
        return updated;
      });
      
      // Build the full events array for DB
      const currentEvents = [...localEvents];
      if (currentEvents[eventIndex]) {
        currentEvents[eventIndex] = { ...currentEvents[eventIndex], finalInstruments: normalized };
      }
      
      // Save to database silently (no refresh)
      const { error } = await supabase
        .from('contact_submissions')
        .update({ event_dates_json: currentEvents })
        .eq('id', inquiry.id);
      
      if (error) throw error;
      
      // Auto-transition status to 'contacted' if currently pending or call_scheduled
      if (['pending', 'call_scheduled'].includes(inquiry.status)) {
        await supabase
          .from('contact_submissions')
          .update({ status: 'contacted' })
          .eq('id', inquiry.id);
        
        // Log the status change
        await logActivity({
          inquiryId: inquiry.id,
          action: 'status_changed',
          description: 'Status changed to Contacted (ensemble activity)',
          metadata: { oldStatus: inquiry.status, newStatus: 'contacted', trigger: 'ensemble_edit' }
        });
      }
      
      // Silent success - no toast to avoid distraction during rapid edits
    } catch (err: any) {
      toast.error(err.message || 'Failed to save instruments');
      // Revert on error by syncing from inquiry
      setLocalEvents(inquiry.event_dates_json || []);
    } finally {
      setSavingInstruments(null);
    }
  };

  // Get final instruments for an event (uses localEvents for optimistic updates)
  const getFinalInstrumentsForEvent = (eventIndex: number): string[] => {
    const event = localEvents[eventIndex];
    const value = event?.finalInstruments;
    if (Array.isArray(value)) {
      return value
        .flatMap((item) =>
          typeof item === "string" && item.includes(",")
            ? item.split(",").map((s) => s.trim()).filter(Boolean)
            : item
        )
        .filter(Boolean);
    }
    if (typeof value === "string") {
      return value.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Convert Song to Track format for audio player
  const songToTrack = (song: Song) => ({
    id: song.spotify_uri || `${song.song_title}-${song.artist}`,
    name: song.song_title || '',
    artist: song.artist || '',
    album: song.album || '',
    imageUrl: song.image_url || '',
    previewUrl: song.preview_url || null,
    spotifyUrl: song.spotify_uri ? `https://open.spotify.com/track/${song.spotify_uri.split(':').pop()}` : '',
    uri: song.spotify_uri || '',
  });

  const handlePlaySong = (song: Song) => {
    const track = songToTrack(song);
    const isSameTrack = currentTrack?.id === track.id;
    
    if (isSameTrack) {
      togglePlayPause();
    } else {
      // playTrack handles YouTube fallback when no preview_url exists (same as taste builder)
      playTrack(track);
    }
  };

  const originCode = manualOriginCode.trim() || viewerOriginAirport?.code;

  const toggleEvent = (idx: number) => {
    const newSet = new Set(expandedEvents);
    if (newSet.has(idx)) newSet.delete(idx);
    else newSet.add(idx);
    setExpandedEvents(newSet);
  };

  // Timeline constants for HTML generation
  const BAGGAGE_TIME = 45;
  const CHECKIN_TIME = 20;
  const FRESHEN_TIME = 20;
  const MEAL_TIME = 20;
  const SETUP_TIME = 120;
  const PACKUP_TIME = 30;
  const AIRPORT_BUFFER = 120;
  const HOTEL_TO_VENUE = 5;

  const formatTimeHtml = (minutes: number): string => {
    const normalized = ((minutes % 1440) + 1440) % 1440;
    const h = Math.floor(normalized / 60);
    const m = normalized % 60;
    const period = h >= 12 ? 'PM' : 'AM';
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const formatDurationHtml = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  // Save travel itinerary as native PDF to documents storage
  const saveItineraryToDocuments = async () => {
    if (!originCode || travelItineraries.length === 0) {
      toast.error("Please select an origin airport first");
      return;
    }
    
    setSavingItinerary(true);
    try {
      // Build timeline data for native PDF generation
      const trips = travelItineraries.map(trip => {
        const arrCode = trip.arrivalAirport?.airport.code || 'TBD';
        const arrName = trip.arrivalAirport?.airport.name || '';
        const depCode = trip.departureAirport?.airport.code || arrCode;
        const firstEvent = trip.events[0];
        const flightTime = trip.flightTime;
        
        const destTz = arrCode ? getAirportTimezoneAbbr(arrCode) : '';
        const originTz = originCode ? getAirportTimezoneAbbr(originCode) : '';
        
        // Calculate times
        const eventStartTime = firstEvent?.startTime || '18:00';
        const [startH, startM] = eventStartTime.split(':').map(Number);
        const eventStartMinutes = startH * 60 + (startM || 0);
        
        const eventEndTime = firstEvent?.endTime || '22:00';
        const [endH, endM] = eventEndTime.split(':').map(Number);
        const eventEndMinutes = endH * 60 + (endM || 0);
        
        const driveToVenueMinutes = trip.arrivalAirport?.distance 
          ? Math.ceil((trip.arrivalAirport.distance / 40) * 60) 
          : 30;
        const driveToAirportMinutes = trip.departureAirport?.distance 
          ? Math.ceil((trip.departureAirport.distance / 40) * 60) 
          : 30;
        
        // Calculate landing time
        let takeoffTime = trip.inboundDepartureMinutes ?? 0;
        if (takeoffTime < 0) takeoffTime = 1440 + takeoffTime;
        
        let landingMinutes = 0;
        let landingDayOffset = 0;
        
        if (flightTime) {
          const landing = calculateLandingTime(takeoffTime, flightTime);
          landingMinutes = landing.landingMinutes;
          landingDayOffset = landing.dayOffset;
        } else {
          landingMinutes = takeoffTime + (flightTime?.flightDurationMinutes || 180);
        }
        
        // Forward timeline from landing
        const leaveAirport = landingMinutes + BAGGAGE_TIME;
        const arriveHotel = leaveAirport + driveToVenueMinutes;
        const arriveVenue = eventStartMinutes - SETUP_TIME;
        const leaveVenue = eventEndMinutes + PACKUP_TIME;
        
        // Red-eye departure
        const redeyeAirport = leaveVenue + driveToAirportMinutes;
        const redeyeFlight = redeyeAirport + AIRPORT_BUFFER;
        
        // Next-day departure
        const checkoutTime = 11 * 60;
        const nextDayAirport = checkoutTime + driveToAirportMinutes;
        const nextDayFlight = nextDayAirport + AIRPORT_BUFFER;
        
        const departureDay = trip.useRedEyeInbound && trip.recommendedArrivalDate 
          ? addDays(trip.recommendedArrivalDate, -1) 
          : trip.recommendedArrivalDate;
        
        const landingDate = departureDay 
          ? addDays(departureDay, landingDayOffset || 0)
          : firstEvent?.venue.date;

        const flightDurationMinutes = flightTime?.flightDurationMinutes || 180;
        const flightHours = Math.floor(flightDurationMinutes / 60);
        const flightMins = flightDurationMinutes % 60;

        // Build timeline events
        const timeline: Array<{ time: string; description: string; isHighlight?: boolean; isMuted?: boolean }> = [
          { time: formatTimeHtml(landingMinutes), description: `✈️ Land at ${arrCode} (${destTz})` },
          { time: formatTimeHtml(leaveAirport), description: `🕐 Baggage → Drive (${formatDurationHtml(driveToVenueMinutes)})`, isMuted: true },
          { time: formatTimeHtml(arriveHotel), description: '🏨 Hotel check-in & prep', isMuted: true },
          { 
            time: formatTimeHtml(arriveVenue), 
            description: `🎵 ${firstEvent?.occasion || 'Event'} — ${formatTimeHtml(eventStartMinutes)} – ${formatTimeHtml(eventEndMinutes)}`, 
            isHighlight: true 
          },
          { time: formatTimeHtml(leaveVenue), description: '⏱️ Pack up & wrap', isMuted: true },
        ];

        return {
          originCode: originCode || '',
          destinationCode: arrCode,
          destinationName: arrName,
          flightDuration: `${flightHours}h ${flightMins > 0 ? `${flightMins}m` : ''}`,
          departureDate: departureDay ? format(departureDay, 'EEE MMM d') : '',
          departureTime: formatTimeHtml(takeoffTime),
          landingDate: landingDate ? format(landingDate, 'EEE MMM d') : '',
          landingTime: formatTimeHtml(landingMinutes),
          destinationTimezone: destTz,
          returnDate: trip.recommendedDepartureDate ? format(trip.recommendedDepartureDate, 'EEE MMM d') : '',
          returnTime: formatTimeHtml(trip.isSameDayDeparture ? redeyeFlight : nextDayFlight),
          isRedEye: trip.useRedEyeInbound,
          eventOccasion: firstEvent?.occasion || 'Event',
          eventTime: `${formatTimeHtml(eventStartMinutes)} – ${formatTimeHtml(eventEndMinutes)}`,
          setupTime: formatTimeHtml(arriveVenue),
          timeline,
        };
      });

      const firstEventDate = travelItineraries[0]?.events[0]?.venue?.date || null;
      const docNumber = generateDocumentNumber('Timeline', firstEventDate);
      
      const timelineData = {
        documentNumber: docNumber,
        clientName: inquiry.name,
        originCode: originCode || '',
        trips,
      };

      // Generate native PDF
      const pdfBlob = generateTimelinePdf(timelineData);
      const filename = `${docNumber}.pdf`;
      const storagePath = `${inquiry.id}/${filename}`;
      
      const { error: uploadError } = await supabase.storage
        .from('contracts')
        .upload(storagePath, pdfBlob, { contentType: 'application/pdf', upsert: false });
        
      if (uploadError) throw uploadError;
      
      // Create document record with event date and occasion for grouping
      const eventDateStr = firstEventDate ? format(firstEventDate, 'yyyy-MM-dd') : null;
      const firstEvent = travelItineraries[0]?.events[0];
      const occasion = firstEvent?.occasion || 'Event';
      
      const { error: dbError } = await supabase
        .from('inquiry_contracts')
        .insert({
          inquiry_id: inquiry.id,
          contract_number: docNumber,
          client_name: inquiry.name,
          client_email: inquiry.email,
          status: 'draft',
          total_amount: 0,
          event_details: { 
            type: 'travel_itinerary', 
            origin: originCode,
            eventDate: eventDateStr,
            occasion: occasion,
          },
          packages: [],
          storage_path: storagePath,
          additional_notes: 'Travel Itinerary Document'
        });
        
      if (dbError) throw dbError;
      
      toast.success("Itinerary saved to Documents");
      // Log activity (hidden from UI but stored for audit)
      logActivity({
        inquiryId: inquiry.id,
        action: 'document_created',
        description: `Travel itinerary ${docNumber} created`,
        metadata: { documentNumber: docNumber, origin: originCode, type: 'travel_itinerary' }
      });
      onRefresh?.();
    } catch (err: any) {
      console.error('Error saving itinerary:', err);
      toast.error(err.message || "Failed to save itinerary");
    } finally {
      setSavingItinerary(false);
    }
  };


  // Get workflow stage info (for progress display only, not gating)
  const { stages } = useWorkflowStages(inquiry, contracts, musicianAssignments);

  return (
    <div className="space-y-3">
      {/* Contact + Financial Row - Always visible */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-card border rounded-lg p-2">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{inquiry.name}</p>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" />{inquiry.email}</span>
                <span className="flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{inquiry.phone}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-2 flex items-center justify-between">
          <div>
            <p className="text-[10px] text-muted-foreground">Total</p>
            <p className="text-lg font-bold text-primary">{formatCurrency(inquiry.total_cost)}</p>
          </div>
          {inquiry.total_savings > 0 && (
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground">Savings</p>
              <p className="text-sm font-semibold text-green-600">−{formatCurrency(inquiry.total_savings)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex items-center gap-2 flex-wrap bg-card border rounded-lg p-2">
        <span className="text-xs text-muted-foreground font-medium mr-1">Actions:</span>
        
        {/* Schedule/Cancel Call Button */}
        {inquiry.scheduled_call_at ? (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-primary/10 rounded-md border border-primary/20">
              <Calendar className="h-3.5 w-3.5 text-primary" />
              {(() => {
                // Convert UTC time to client's timezone for accurate display
                const clientTz = inquiry.user_timezone || 'America/Chicago';
                const utcDate = new Date(inquiry.scheduled_call_at);
                const zonedDate = toZonedTime(utcDate, clientTz);
                const tzAbbr = (() => {
                  try {
                    const formatter = new Intl.DateTimeFormat('en-US', {
                      timeZone: clientTz,
                      timeZoneName: 'short'
                    });
                    const parts = formatter.formatToParts(utcDate);
                    return parts.find(p => p.type === 'timeZoneName')?.value || '';
                  } catch { return ''; }
                })();
                return (
                  <div className="text-xs">
                    <span className="font-medium text-primary">
                      {format(zonedDate, "MMM d")}
                    </span>
                    <span className="text-muted-foreground ml-1">
                      {format(zonedDate, "h:mm a")}
                    </span>
                    {tzAbbr && (
                      <span className="text-muted-foreground ml-1 text-[10px]">({tzAbbr})</span>
                    )}
                  </div>
                );
              })()}
            </div>
            {onCancelCall && (
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 text-xs gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={onCancelCall}
              >
                <X className="h-3.5 w-3.5" />
                Cancel Call
              </Button>
            )}
          </div>
        ) : (
          onScheduleCall && (
            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 text-xs gap-1.5"
              onClick={onScheduleCall}
            >
              <PhoneCall className="h-3.5 w-3.5" />
              Schedule Call
            </Button>
          )
        )}

        {/* Inquiry-Level Agreement Button - Shows when ALL events have complete ensembles */}
        {(() => {
          // Check if all events have their ensemble requirements met
          const allEnsemblesComplete = localEvents.every((event: any, idx: number) => {
            const eventPackages = getPackagesForEvent(idx);
            const packageMusicians = eventPackages.flatMap((pkg: Package) => {
              if (Array.isArray(pkg.musicians)) return pkg.musicians;
              if (typeof pkg.musicians === 'string') {
                return pkg.musicians.split(',').map(m => m.trim()).filter(Boolean);
              }
              return [];
            });
            const ensembleSize = eventPackages[0]?.ensembleSize || packageMusicians.length || 6;
            const finalInstruments = getFinalInstrumentsForEvent(idx);
            return finalInstruments.length === ensembleSize;
          });
          
          if (allEnsemblesComplete && onOpenContract) {
            return (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5 border-primary text-primary hover:bg-primary/10"
                onClick={onOpenContract}
              >
                <FileText className="h-3.5 w-3.5" />
                Create Agreement
              </Button>
            );
          }
          return null;
        })()}
        
      </div>

      {/* Travel Itinerary - Always visible */}
      {travelItineraries.length > 0 && (
        <Collapsible open={travelExpanded} onOpenChange={setTravelExpanded}>
          <div className="bg-card border rounded-lg overflow-hidden">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between px-3 py-2 hover:bg-muted/30 cursor-pointer">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${travelExpanded ? '' : '-rotate-90'}`} />
                  <Plane className="h-4 w-4" />
                  <span className="text-sm font-medium">Travel Itinerary</span>
                  {loadingVenues && <Loader2 className="h-3 w-3 animate-spin" />}
                  {!travelExpanded && itinerarySummary && (
                    <div className="flex items-center gap-3 ml-2 text-xs">
                      <span className="text-foreground font-medium">{itinerarySummary.totalHours} total</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">{itinerarySummary.travelHours} travel</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">{itinerarySummary.eventHours} event</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={saveItineraryToDocuments}
                    disabled={savingItinerary || !originCode}
                  >
                    {savingItinerary ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                    Save to Docs
                  </Button>
                  <span className="text-xs text-muted-foreground">Origin:</span>
                  <AirportSearch
                    value={manualOriginCode || viewerOriginAirport?.code || ''}
                    onChange={setManualOriginCode}
                    placeholder="Select airport..."
                    className="w-28"
                  />
                </div>
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <div className="p-3 pt-0 space-y-4">
            {travelItineraries.map((trip, idx) => {
              const arrCode = trip.arrivalAirport?.airport.code;
              const arrName = trip.arrivalAirport?.airport.name;
              const depCode = trip.departureAirport?.airport.code;
              const depName = trip.departureAirport?.airport.name;
              const isLocal = originCode && arrCode && originCode === arrCode;
              const isRoundTrip = arrCode === depCode;
              const driveTimeToVenueMinutes = trip.arrivalAirport?.distance ? Math.ceil((trip.arrivalAirport.distance / 40) * 60) : 30; // ~40mph avg
              const driveTimeFromVenueMinutes = trip.departureAirport?.distance ? Math.ceil((trip.departureAirport.distance / 40) * 60) : 30;
              
              // Calculate timeline details based on first event of trip
              const firstEvent = trip.events[0];
              const lastEvent = trip.events[trip.events.length - 1];
              const eventStartTime = firstEvent?.startTime || '18:00';
              const eventEndTime = lastEvent?.endTime || '23:00';
              
              // Parse times for calculations
              const parseTime = (timeStr: string) => {
                const [h, m] = timeStr.split(':').map(Number);
                return h * 60 + (m || 0);
              };
              const formatTimeFromMinutes = (minutes: number) => {
                const isNextDay = minutes >= 1440;
                const adjustedMinutes = minutes % 1440;
                const h = Math.floor(adjustedMinutes / 60) % 24;
                const m = adjustedMinutes % 60;
                const period = h >= 12 ? 'PM' : 'AM';
                const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
                const nextDaySuffix = isNextDay ? ' (+1)' : '';
                return `${displayH}:${m.toString().padStart(2, '0')} ${period}${nextDaySuffix}`;
              };

               // Format a time-of-day WITHOUT the "(+1)" suffix. Use this when we already
               // render an explicit day header (e.g. "Fri, Jan 30").
               const formatTimeNoDaySuffix = (minutes: number) => {
                 const normalized = ((minutes % 1440) + 1440) % 1440;
                 return formatTimeFromMinutes(normalized);
               };
              
              const eventStartMinutes = parseTime(eventStartTime);
              const eventEndMinutes = parseTime(eventEndTime);
              
              // ===== DETAILED TIMELINE CALCULATIONS =====
              // User-specified durations
              const BAGGAGE_CUSTOMS_TIME = 45; // 45 min for baggage claim + customs (international) or 30 domestic
              const HOTEL_CHECKIN_TIME = 20; // 20 min hotel check-in
              const FRESHEN_UP_TIME = 20; // 20 min freshen up
              const PRE_EVENT_MEAL_TIME = 20; // 20 min meal before event
              const SETUP_SOUNDCHECK_TIME = 120; // 2 hours before start time for setup/soundcheck/coordination
              const PACK_EAT_GOODBYE_TIME = 30; // 30 min after event ends for pack up, eat, say goodbyes
              const AIRPORT_CHECKIN_BUFFER = 120; // 2 hours before flight for airport security/checkin
              const driveMinutes = driveTimeToVenueMinutes;
              const driveToAirportMinutes = driveTimeFromVenueMinutes;
              
              // ===== ARRIVAL DAY TIMELINE (working backwards from event start) =====
              // Hotel is assumed within 1 mile of venue (~5 min drive)
              const HOTEL_TO_VENUE_DRIVE = 5; // 5 min drive from hotel to venue
              // Venue arrival must be 2 hours before performance
              const arrivalAtVenueMinutes = eventStartMinutes - SETUP_SOUNDCHECK_TIME;
              // Drive time from hotel to venue (only 5 min since hotel near venue)
              const leaveHotelForVenueMinutes = arrivalAtVenueMinutes - HOTEL_TO_VENUE_DRIVE;
              // Meal before leaving
              const preMealMinutes = leaveHotelForVenueMinutes - PRE_EVENT_MEAL_TIME;
              // Freshen up time
              const freshenUpStartMinutes = preMealMinutes - FRESHEN_UP_TIME;
              // Hotel check-in duration
              const hotelCheckinStartMinutes = freshenUpStartMinutes - HOTEL_CHECKIN_TIME;
              // Arrive at hotel
              const arriveHotelMinutes = hotelCheckinStartMinutes;
              // Drive from airport to hotel (full airport-to-venue distance since hotel is near venue)
              const leaveAirportMinutes = arriveHotelMinutes - driveMinutes;
              // Baggage/customs - this is the REQUIRED landing time (destination local)
              const requiredLandingMinutes = leaveAirportMinutes - BAGGAGE_CUSTOMS_TIME;
              
              // ===== TIMEZONE-AWARE ACTUAL LANDING CALCULATION =====
              // Calculate actual landing time using SAME logic as FlightSearchLinks
              // This ensures timeline and flight info box show IDENTICAL times
              const flightTimeData = trip.flightTime;
              
              // Calculate flight duration for fallback (same as FlightSearchLinks)
              const flightDuration = flightTimeData?.flightDurationMinutes ?? 180;
              
              // Get optimal takeoff time using EXACT same fallback as FlightSearchLinks (line 724)
              // FlightSearchLinks: trip.inboundDepartureMinutes ?? (landingMinutes - flightDuration - 30)
              // But here "landingMinutes" hasn't been calculated yet, so use requiredLandingMinutes
              let takeoffTimeForCalc = trip.inboundDepartureMinutes ?? (requiredLandingMinutes - flightDuration - 30);
              
              // Convert negative departure (previous day) to positive time value
              // e.g., -120 = 10 PM previous day → 1440 - 120 = 1320
              if (takeoffTimeForCalc < 0) {
                takeoffTimeForCalc = 1440 + takeoffTimeForCalc;
              }
              
              // Calculate actual landing from departure + flight time + timezone offset
              let actualLandingMinutes = requiredLandingMinutes; // Fallback
              let actualLandingDayOffset = 0;
              
              if (flightTimeData) {
                const landing = calculateLandingTime(takeoffTimeForCalc, flightTimeData);
                actualLandingMinutes = landing.landingMinutes;
                actualLandingDayOffset = landing.dayOffset;
              }
              
              // Use actual landing for timeline display
              const landingMinutes = actualLandingMinutes;
              // Day offset is used for date header calculation only (not for time display)
              
              // Recalculate timeline from actual landing (forward from landing, not backwards from event)
              // This is what actually happens when the flight lands
              const actualLeaveAirportMinutes = landingMinutes + BAGGAGE_CUSTOMS_TIME;
              const actualArriveHotelMinutes = actualLeaveAirportMinutes + driveMinutes;
              const actualCheckinCompleteMinutes = actualArriveHotelMinutes + HOTEL_CHECKIN_TIME;
              const actualFreshenUpCompleteMinutes = actualCheckinCompleteMinutes + FRESHEN_UP_TIME;
              const actualMealCompleteMinutes = actualFreshenUpCompleteMinutes + PRE_EVENT_MEAL_TIME;
              const actualLeaveForVenueMinutes = actualMealCompleteMinutes;
              const actualArriveVenueMinutes = actualLeaveForVenueMinutes + HOTEL_TO_VENUE_DRIVE;
              
              // ===== DEPARTURE TIMELINE (working forward from event end) =====
              // Pack up, eat, say goodbyes after event ends
              const leaveVenueMinutes = eventEndMinutes + PACK_EAT_GOODBYE_TIME;
              
              // Red-eye path: Skip hotel, go directly to airport after event
              // leaveVenueMinutes → drive to airport → security → red-eye flight
              const redeyeArriveAirportMinutes = leaveVenueMinutes + driveToAirportMinutes;
              const redeyeFlightMinutes = redeyeArriveAirportMinutes + AIRPORT_CHECKIN_BUFFER;
              
              // Next-day path: Return to hotel, sleep, checkout next morning
              const arriveHotelAfterEventMinutes = leaveVenueMinutes + HOTEL_TO_VENUE_DRIVE;
              const checkoutMinutes = 11 * 60; // 11 AM checkout
              const leaveHotelForAirportMinutes = checkoutMinutes;
              const arriveAirportMinutes = leaveHotelForAirportMinutes + driveToAirportMinutes;
              const earliestFlightMinutes = arriveAirportMinutes + AIRPORT_CHECKIN_BUFFER;
              
              return (
                <div key={idx} className="space-y-3">
                  {travelItineraries.length > 1 && (
                    <Badge variant="secondary" className="text-xs">Trip {trip.tripNumber} of {travelItineraries.length}</Badge>
                  )}
                  
                  {isLocal ? (
                    /* Local Event - No flight needed */
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                        <Car className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Local Event</p>
                          <p className="text-xs text-muted-foreground">
                            {trip.arrivalAirport?.distance} miles • ~{driveTimeToVenueMinutes} min drive
                          </p>
                        </div>
                      </div>
                      
                      {/* Local Event Day Timeline */}
                      <div className="border-l-2 border-primary/30 ml-2 pl-3 space-y-1.5">
                        <div className="flex items-center gap-2 text-xs">
                          <Utensils className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground w-16">{formatTimeFromMinutes(preMealMinutes)}</span>
                          <span>Meal ({PRE_EVENT_MEAL_TIME} min)</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <Car className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground w-16">{formatTimeFromMinutes(leaveHotelForVenueMinutes)}</span>
                          <span>Leave for venue ({driveTimeToVenueMinutes} min drive)</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <MapPinned className="h-3 w-3 text-primary" />
                          <span className="text-muted-foreground w-16">{formatTimeFromMinutes(arrivalAtVenueMinutes)}</span>
                          <span className="font-medium">Arrive venue • Soundcheck/Setup (2 hrs)</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs bg-primary/10 -ml-3 pl-3 py-1 rounded-r">
                          <Music className="h-3 w-3 text-primary" />
                          <span className="font-semibold text-primary w-16">{formatTimeFromMinutes(eventStartMinutes)}</span>
                          <span className="font-semibold text-primary">Performance starts</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs bg-primary/10 -ml-3 pl-3 py-1 rounded-r">
                          <Check className="h-3 w-3 text-primary" />
                          <span className="font-semibold w-16">{formatTimeFromMinutes(eventEndMinutes)}</span>
                          <span className="font-semibold">Performance ends</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <Timer className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground w-16">{formatTimeFromMinutes(leaveVenueMinutes)}</span>
                          <span>Pack up, eat, say goodbyes ({PACK_EAT_GOODBYE_TIME} min)</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Non-local: Use condensed TravelTimeline component */
                    (() => {
                      const firstEventDate = trip.events[0]?.venue?.date;
                      const isSameDayArrival = !!(firstEventDate && trip.recommendedArrivalDate && 
                        format(firstEventDate, 'yyyy-MM-dd') === format(trip.recommendedArrivalDate, 'yyyy-MM-dd'));
                      
                      // Calculate flight duration
                      const flightDuration = originCode && arrCode ? (() => {
                        const origin = majorAirports.find(a => a.code === originCode);
                        const dest = majorAirports.find(a => a.code === arrCode);
                        if (!origin || !dest) return 180;
                        const distance = calculateDistance(origin.lat, origin.lng, dest.lat, dest.lng);
                        return Math.round((distance / 500) * 60) + 45;
                      })() : 180;
                      
                      return (
                        <TravelTimeline
                          originCode={originCode}
                          arrCode={arrCode}
                          arrName={arrName}
                          depCode={depCode}
                          flightDurationMinutes={flightDuration}
                          driveToVenueMinutes={driveTimeToVenueMinutes}
                          driveToAirportMinutes={driveTimeFromVenueMinutes}
                          distanceToVenue={trip.arrivalAirport?.distance}
                          distanceToAirport={trip.departureAirport?.distance}
                          eventStartMinutes={eventStartMinutes}
                          eventEndMinutes={eventEndMinutes}
                          eventOccasion={firstEvent?.occasion || 'Event'}
                          eventDate={firstEventDate}
                          recommendedArrivalDate={trip.recommendedArrivalDate}
                          recommendedDepartureDate={trip.recommendedDepartureDate}
                          isSameDayArrival={isSameDayArrival}
                          isSameDayDeparture={!!trip.isSameDayDeparture}
                          useRedEyeInbound={!!trip.useRedEyeInbound}
                          inboundDepartureMinutes={trip.inboundDepartureMinutes}
                          flightTime={trip.flightTime}
                          isRoundTrip={isRoundTrip}
                        />
                      );
                    })()
                  )}
                </div>
              );
            })}
          </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {/* Events - Collapsible Cards - Always visible */}
      <div className="space-y-2">
        {events.map((event: any, eventIndex: number) => {
          const analysis = getAnalysisForEvent(eventIndex);
          const eventPackages = getPackagesForEvent(eventIndex);
          const eventSongs = getSongsForEvent(eventIndex);
          const eventDocuments = getDocumentsForEvent(eventIndex);
          const hasAnalysis = analysis && Object.keys(analysis).length > 0;
          const isExpanded = expandedEvents.has(eventIndex);
          const eventTotal = eventPackages.reduce((sum, pkg) => sum + (pkg.price || 0), 0);

          // Get final instruments and ensemble size for collapsed preview
          const finalInstruments = getFinalInstrumentsForEvent(eventIndex);
          const packageMusicians = eventPackages.flatMap((pkg: Package) => {
            if (Array.isArray(pkg.musicians)) return pkg.musicians;
            if (typeof pkg.musicians === 'string') {
              return pkg.musicians.split(',').map(m => m.trim()).filter(Boolean);
            }
            return [];
          });
          const ensembleSize = eventPackages[0]?.ensembleSize || packageMusicians.length || 6;
          // Use strict equality: ensemble is met ONLY when selected count equals required size
          const ensembleMet = finalInstruments.length === ensembleSize;
          const venueAddress = event.venueAddress || event.venue || inquiry.venue_address;

          return (
            <Collapsible key={eventIndex} open={isExpanded} onOpenChange={() => toggleEvent(eventIndex)}>
              <div className="border rounded-lg overflow-hidden">
                {/* Event Header - Always visible */}
                <CollapsibleTrigger asChild>
                  <div className="flex flex-col px-2.5 py-2 bg-muted/30 hover:bg-muted/50 cursor-pointer gap-1.5">
                    {/* Header Row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <Badge variant="outline" className="font-mono text-[10px] h-5 w-5 p-0 flex items-center justify-center shrink-0 mt-0.5">
                          {eventIndex + 1}
                        </Badge>
                        <div className="min-w-0 flex-1 space-y-0.5">
                          {/* Row 1: Occasion + Package + Ensemble */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-sm">{event.occasion || 'Event'}</span>
                            {eventPackages.length > 0 && (
                              <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{eventPackages[0]?.name}</Badge>
                            )}
                            {finalInstruments.length > 0 && (
                              <div className="flex items-center gap-1">
                                {finalInstruments.map((inst, i) => (
                                  <Badge key={i} variant="outline" className={`text-[9px] h-4 px-1 ${ensembleMet ? 'bg-primary/10 border-primary/30 text-primary' : ''}`}>
                                    {inst}
                                  </Badge>
                                ))}
                                <span className={`text-[9px] font-mono ${ensembleMet ? 'text-primary' : 'text-muted-foreground'}`}>
                                  ({finalInstruments.length}/{ensembleSize})
                                </span>
                              </div>
                            )}
                          </div>
                          {/* Row 2: Date/Time + Address */}
                          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                            <span>{safeFormatEventDate(event.date, "MMM d")} • {event.startTime}–{event.endTime}</span>
                            <span>•</span>
                            <span>{venueAddress}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right side: Price + Tech Rider (no Contract button - moved to inquiry level) */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        {eventTotal > 0 && <span className="text-xs font-semibold text-primary">{formatCurrency(eventTotal)}</span>}
                        {/* Tech Rider only shows when ensemble is EXACTLY met (strict equality) */}
                        {finalInstruments.length === ensembleSize && (
                          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                            <TechRiderGenerator
                              instruments={finalInstruments}
                              eventName={event.occasion || `Event ${eventIndex + 1}`}
                              eventDate={safeFormatEventDate(event.date, 'MMM d, yyyy')}
                              venue={venueAddress}
                              inquiryId={inquiry.id}
                              clientName={inquiry.name}
                              clientEmail={inquiry.email}
                              isComplete={finalInstruments.length === ensembleSize}
                              onSaved={onRefresh}
                            />
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); runAnalysisForEvent(eventIndex); }}
                          disabled={runningAnalysis !== null}
                          className="h-5 w-5 p-0"
                        >
                          {runningAnalysis === eventIndex ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                    
                    {/* AI Analysis row - always visible */}
                    {hasAnalysis && (
                      <div className="pl-7 space-y-1 text-[10px]" onClick={(e) => e.stopPropagation()}>
                        {/* Culture + Confidence + All Genres */}
                        <div className="flex items-center gap-1.5 flex-wrap cursor-pointer" onClick={() => toggleEvent(eventIndex)}>
                          <Sparkles className="h-3 w-3 text-primary shrink-0" />
                          {analysis.primaryCulture && (
                            <span className="font-medium text-foreground">{analysis.primaryCulture}</span>
                          )}
                          {analysis.confidence && (
                            <span className="text-[9px] text-muted-foreground">({analysis.confidence}%)</span>
                          )}
                          {/* All genres */}
                          {(() => {
                            const clientVibes = event.vibes || [];
                            const aiVibes = analysis?.vibes || [];
                            const aiGenres = analysis?.genres || [];
                            const allTags = [...new Set([...clientVibes, ...aiVibes, ...aiGenres])].filter(Boolean);
                            if (allTags.length === 0) return null;
                            return allTags.map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-[9px] h-4 px-1">{tag}</Badge>
                            ));
                          })()}
                        </div>
                        
                        {/* AI Message - full text, always visible */}
                        {(analysis.customerMessage || analysis.reasoning || analysis.analysis) && (
                          <p className="text-muted-foreground leading-snug cursor-pointer" onClick={() => toggleEvent(eventIndex)}>
                            {analysis.customerMessage || analysis.reasoning || analysis.analysis}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CollapsibleTrigger>

                {/* Expanded Content */}
                <CollapsibleContent>
                  <div className="p-2 space-y-2 border-t">
                    {(() => {
                      // Get ensemble size from package
                      const packageMusicians = eventPackages.flatMap((pkg: Package) => {
                        if (Array.isArray(pkg.musicians)) return pkg.musicians;
                        if (typeof pkg.musicians === 'string') {
                          return pkg.musicians.split(',').map(m => m.trim()).filter(Boolean);
                        }
                        return [];
                      });
                      const ensembleSize = eventPackages[0]?.ensembleSize || packageMusicians.length || 6;
                      
                      return (
                        <FinalInstrumentEditor
                          eventIndex={eventIndex}
                          recommendedInstruments={analysis?.recommendedInstruments || []}
                          packageInstruments={packageMusicians}
                          requiredEnsembleSize={ensembleSize}
                          finalInstruments={getFinalInstrumentsForEvent(eventIndex)}
                          onSave={(instruments) => saveFinalInstruments(eventIndex, instruments)}
                          onPushToContract={onOpenContract}
                          isLoading={savingInstruments === eventIndex}
                          eventName={event.occasion || `Event ${eventIndex + 1}`}
                          eventDate={safeFormatEventDate(event.date, 'MMM d, yyyy')}
                          venue={event.venueAddress || event.venue || inquiry.venue_address}
                          inquiryId={inquiry.id}
                          clientName={inquiry.name}
                          clientEmail={inquiry.email}
                          onDocumentSaved={onRefresh}
                        />
                      );
                    })()}

                    {/* Songs - Always visible */}
                    {eventSongs.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
                          <Music className="h-3 w-3" /> {eventSongs.length} songs
                        </p>
                        
                        {/* Inline Mini Audio Player - inside songs section */}
                        {currentTrack && eventSongs.some(s => (s.spotify_uri || `${s.song_title}-${s.artist}`) === currentTrack.id) && (
                          <MiniAudioPlayer
                            trackName={currentTrack.name}
                            artistName={currentTrack.artist}
                            imageUrl={currentTrack.imageUrl}
                            progress={progress}
                            duration={duration}
                            currentTime={currentTime}
                            source={currentTrack.youtubeId ? 'youtube' : 'spotify'}
                            isPlaying={isPlaying}
                            onStop={stopPlayback}
                            onTogglePlayPause={togglePlayPause}
                          />
                        )}
                        
                        <div className="bg-card border rounded divide-y max-h-40 overflow-y-auto">
                          {eventSongs.map((song: Song, idx: number) => {
                            const trackId = song.spotify_uri || `${song.song_title}-${song.artist}`;
                            const isCurrentTrack = currentTrack?.id === trackId;
                            
                            return (
                              <div 
                                key={idx} 
                                className={`flex items-center gap-2 p-1.5 hover:bg-muted/50 cursor-pointer transition-colors ${isCurrentTrack ? 'bg-primary/10' : ''}`}
                                onClick={() => handlePlaySong(song)}
                              >
                                {/* Album Art with play overlay - always clickable (YouTube fallback) */}
                                <div className="relative w-8 h-8 rounded overflow-hidden shrink-0 bg-muted">
                                  {song.image_url ? (
                                    <img src={song.image_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Music className="h-3 w-3 text-muted-foreground" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                    {isCurrentTrack && isPlaying ? (
                                      <Pause className="h-3 w-3 text-white" />
                                    ) : (
                                      <Play className="h-3 w-3 text-white" />
                                    )}
                                  </div>
                                </div>
                                
                                {/* Track info */}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-medium truncate ${isCurrentTrack ? 'text-primary' : ''}`}>
                                    {song.song_title}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground truncate">{song.artist}</p>
                                </div>
                                
                                {/* Spotify link */}
                                {song.spotify_uri && (
                                  <a
                                    href={`https://open.spotify.com/track/${song.spotify_uri.split(':').pop()}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-500 shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Documents - Always visible */}
                    {eventDocuments.length > 0 && (
                      <div className="space-y-2 pt-2 border-t">
                        <p className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
                          <FileText className="h-3 w-3" /> {eventDocuments.length} document{eventDocuments.length !== 1 ? 's' : ''}
                        </p>
                        <div className="space-y-1.5">
                          {eventDocuments.map((doc: any) => {
                            const docTypeLabel = getDocumentTypeLabel(doc.contract_number);
                            const isItinerary = doc.contract_number?.startsWith('Timeline-') || doc.contract_number?.startsWith('ITIN-');
                            const isTechRider = doc.contract_number?.startsWith('TechRider-') || doc.contract_number?.startsWith('TECH-');
                            
                            return (
                              <div 
                                key={doc.id} 
                                className="flex items-center justify-between p-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className={`p-1 rounded ${
                                    isItinerary ? 'bg-primary/10 text-primary' : 
                                    isTechRider ? 'bg-accent text-accent-foreground' :
                                    'bg-muted text-muted-foreground'
                                  }`}>
                                    {isItinerary ? <Plane className="h-3 w-3" /> : 
                                     isTechRider ? <Music className="h-3 w-3" /> : 
                                     <FileText className="h-3 w-3" />}
                                  </div>
                                  <div className="min-w-0">
                                    {/* Show full document number from Files, not stripped label */}
                                    <p className="text-xs font-medium truncate">{doc.contract_number}</p>
                                    <div className="flex items-center gap-1.5">
                                      <Badge variant="outline" className={`text-[9px] h-4 ${
                                        isItinerary ? 'border-primary/30 text-primary' : 
                                        isTechRider ? 'border-accent/30' : ''
                                      }`}>
                                        {docTypeLabel}
                                      </Badge>
                                      <Badge 
                                        variant={doc.status === 'draft' ? 'outline' : 'default'} 
                                        className={`text-[9px] h-4 ${
                                          doc.status === 'signed' ? 'bg-green-600 text-white' : 
                                          doc.status === 'sent' ? 'bg-blue-500/20 text-blue-700 border-blue-500/30' : ''
                                        }`}
                                      >
                                        {doc.status === 'signed' ? '✓ Signed' : doc.status === 'sent' ? '📧 Sent' : 'Draft'}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                                
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                      <MoreHorizontal className="h-3 w-3" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-popover border shadow-lg z-50">
                                    <DropdownMenuItem 
                                      onClick={() => isItinerary && onEditItinerary ? onEditItinerary(doc) : onEditContract?.(doc)} 
                                      className="cursor-pointer text-xs"
                                    >
                                      <Pencil className="mr-2 h-3 w-3" /> {isItinerary ? 'Add Details' : 'Edit'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => onPreviewContract?.(doc.storage_path, doc.contract_number)} 
                                      className="cursor-pointer text-xs"
                                    >
                                      <Eye className="mr-2 h-3 w-3" /> Preview
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => onDownloadContract?.(doc.storage_path, doc.contract_number)} 
                                      className="cursor-pointer text-xs"
                                    >
                                      <Download className="mr-2 h-3 w-3" /> Download
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => onAttachToEmail?.(doc)} 
                                      className="cursor-pointer text-xs"
                                    >
                                      <Mail className="mr-2 h-3 w-3" /> Attach to Email
                                    </DropdownMenuItem>
                                    {!isItinerary && !isTechRider && doc.status !== 'signed' && (
                                      <DropdownMenuItem 
                                        onClick={() => onSendForSignature?.(doc)} 
                                        className="cursor-pointer text-xs text-primary"
                                      >
                                        <Send className="mr-2 h-3 w-3" /> Send for Signature
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem 
                                      onClick={() => onDeleteContract?.(doc)} 
                                      className="cursor-pointer text-xs text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="mr-2 h-3 w-3" /> Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>

    </div>
  );
}
